import React, { useCallback, useEffect, useState } from "react";
import {
  Action,
  Condition,
  getDataRulesMeta,
  getRule,
  Rule,
  RuleGroup,
  RuleType,
  saveRule,
} from "../../api/ruleengine";

import { useSnackbar } from "notistack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { RuleCondition } from "./RuleCondition";
import { RuleAction } from "./RuleAction";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import classes from "./RuleBuilder.module.css";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";

const getDefaultAction = (actions: Action[]): Action => {
  const preferredAction =
    actions?.find((action) => action.action_type === "SEND_TO_MANUAL_REVIEW") ||
    actions?.[0];
  return { action_type: preferredAction?.action_type, action_value: "" };
};

const isBlankExpectation = (expectation: any) =>
  expectation === "" ||
  expectation === null ||
  expectation === undefined ||
  Number.isNaN(expectation);

const getRuleValidationMessage = (
  ruleDetails: Rule,
  conditionStatementsBySubject: any,
  availableActions: Action[]
): string | null => {
  if (!ruleDetails) {
    return null;
  }
  if (!ruleDetails.description?.trim()) {
    return "Add a description before saving.";
  }
  const conditions = ruleDetails.conditions || [];
  const actions = ruleDetails.actions || [];
  const invalidCondition = conditions.find((condition) => {
    const statementMeta = conditionStatementsBySubject?.[
      condition.subject
    ]?.statements?.find((statement) => statement.name === condition.statement);
    return (
      statementMeta?.requires_input === true &&
      isBlankExpectation(condition.expectation)
    );
  });
  if (invalidCondition) {
    return "Add a value to every condition before saving.";
  }
  const invalidAction = actions.find((action) => {
    const actionMeta = availableActions?.find(
      (availableAction) => availableAction.action_type === action.action_type
    );
    return actionMeta?.requires_input === true && !action.action_value?.trim();
  });
  if (invalidAction) {
    return "Add a value to every action before saving.";
  }
  if (conditions.length === 0 || actions.length === 0) {
    return "A rule needs at least one condition and one action.";
  }
  return null;
};

export function RuleGroupBuilder(
  props: Readonly<{
    ruleGroup: RuleGroup;
    onRuleGroupChange?(ruleGroupDetails): void;
    integratedSystemId: number;
  }>
) {
  const { ruleGroup, onRuleGroupChange } = props;
  const [selectedRuleGroup, setSelectedRuleGroup] =
    useState<RuleGroup>(ruleGroup);

  const updateRuleGroup = (newRule: Rule, index: number) => {
    let newRules = selectedRuleGroup.rules;
    newRules[index] = newRule;
    setSelectedRuleGroup({ ...selectedRuleGroup, rules: newRules });
  };

  useEffect(() => {
    if (onRuleGroupChange) {
      onRuleGroupChange(selectedRuleGroup);
    }
  }, [selectedRuleGroup]);

  return (
    <>
      {selectedRuleGroup?.rules?.map((ruleDetails, index) => {
        return (
          <Accordion
            key={`${ruleDetails.id}`}
            defaultExpanded={index === selectedRuleGroup.rules.length - 1}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{ruleDetails.description}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <RuleBuilder
                  integratedSystemId={props.integratedSystemId}
                  rule={ruleDetails}
                  disableSaveButton={true}
                  onRuleUpdate={(newRule) => updateRuleGroup(newRule, index)}
                  ruleType={RuleType.CHANGES}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        );
      })}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ margin: "2px", padding: "0em 0.5em" }}
          onClick={() =>
            setSelectedRuleGroup({
              ...selectedRuleGroup,
              rules: [
                ...selectedRuleGroup.rules,
                selectedRuleGroup.rules.slice(-1)[0],
              ],
            })
          }
        >
          Add rule
        </Button>
      </div>
    </>
  );
}

export function RuleBuilder(
  props: Readonly<{
    ruleId?: number;
    rule?: Rule;
    ruleGroupId?: number;
    onRuleUpdate?(ruleDetails): void;
    onRuleSaved?(ruleDetails): void;
    disableSaveButton?: boolean;
    disableRuleEdits?: boolean;
    ruleType: RuleType;
    integratedSystemId?: number;
  }>
) {
  const {
    ruleId,
    rule,
    ruleGroupId,
    onRuleUpdate,
    onRuleSaved,
    disableSaveButton,
    disableRuleEdits,
    ruleType,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [ruleDetails, setRuleDetails] = useState(rule);
  const [conditionStatementsBySubject, setConditionStatementsBySubject] =
    useState({});

  const [availableActions, setAvailableActions] = useState<Action[]>([]);
  const [ruleSaving, setRuleSaving] = useState<Boolean>(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const resolvedRuleGroupId =
    ruleGroupId || rule?.rule_group_id || ruleDetails?.rule_group_id;

  useEffect(() => {
    if (onRuleUpdate) {
      onRuleUpdate(ruleDetails);
    }
  }, [ruleDetails]);

  useEffect(() => {
    if (rule) {
      setRuleDetails(rule);
    }
  }, [rule]);

  useEffect(() => {
    if (ruleId) {
      getRule(ruleId)
        .then((ruleResponse) => {
          setRuleDetails(ruleResponse);
        })
        .catch((err) => console.log(err));
    }
  }, [ruleId]);

  useEffect(() => {
    if (ruleId && !resolvedRuleGroupId) {
      return;
    }

    let fabricatedRuleDetails = {
      pre_conditions: undefined,
      active: true,
      description: "",
      verb: "ALL",
      conditions: [],
      actions: [],
      rule_group_id: resolvedRuleGroupId,
    };

    getDataRulesMeta(resolvedRuleGroupId, ruleType)
      .then((response) => {
        let statementsBySubject = {};
        response.conditions.forEach((item) => {
          statementsBySubject[item.name] = item;
        });
        setConditionStatementsBySubject(statementsBySubject);

        setAvailableActions(response.actions);
        fabricatedRuleDetails.conditions = [
          getDefaultRuleCondition(statementsBySubject),
        ];

        fabricatedRuleDetails.actions = [getDefaultAction(response.actions)];

        if (!ruleId && !rule) {
          setRuleDetails(fabricatedRuleDetails as Rule);
        }
      })
      .catch((err) => console.log(err));
  }, [resolvedRuleGroupId, ruleId, rule, ruleType]);

  const getDefaultRuleCondition = (statementsBySubject) => {
    let firstEntry = Object.entries(statementsBySubject)[0];
    return {
      subject: firstEntry[0],
      statement: (firstEntry[1] as any).statements[0].name,
      expectation: "",
      additional_attribute_id: (firstEntry[1] as any).attribute_options?.[0]
        ?.id,
    };
  };

  const onAddNewCondition = useCallback(
    (_condition: Condition, currentIndex: number) => {
      return (suppliedCondition) => {
        setRuleDetails({
          ...ruleDetails,
          conditions: [
            ...ruleDetails.conditions.slice(0, currentIndex),
            suppliedCondition,
            ...ruleDetails.conditions.slice(currentIndex),
          ],
        });
      };
    },
    [ruleDetails]
  );

  const onAddNewAction = (_action: Action, currentIndex: number) => {
    return (suppliedAction) => {
      setRuleDetails((currentRuleDetails) => {
        if (!currentRuleDetails) {
          return currentRuleDetails;
        }
        const newAction =
          availableActions.length > 0
            ? getDefaultAction(availableActions)
            : { ...suppliedAction, action_value: "" };
        return {
          ...currentRuleDetails,
          actions: [
            ...currentRuleDetails.actions.slice(0, currentIndex + 1),
            newAction,
            ...currentRuleDetails.actions.slice(currentIndex + 1),
          ],
        };
      });
    };
  };

  const onRemoveCondition = (_condition: Condition, conditionIndex: number) => {
    if (ruleDetails.conditions.length > 1) {
      return (_suppliedCondition) => {
        let updatedConditions = [
          ...ruleDetails.conditions.slice(0, conditionIndex),
          ...ruleDetails.conditions.slice(conditionIndex + 1),
        ];
        setRuleDetails({
          ...ruleDetails,
          conditions: updatedConditions,
        });
      };
    }
  };

  const onRemoveAction = (_action: Action, currentIndex: number) => {
    if (ruleDetails.actions.length > 1) {
      return (_suppliedAction) => {
        setRuleDetails({
          ...ruleDetails,
          actions: [
            ...ruleDetails.actions.slice(0, currentIndex),
            ...ruleDetails.actions.slice(currentIndex + 1),
          ],
        });
      };
    }
  };

  const validationMessage = getRuleValidationMessage(
    ruleDetails,
    conditionStatementsBySubject,
    availableActions
  );
  const showDescriptionError =
    descriptionTouched && !ruleDetails?.description?.trim();

  const onRuleSave = () => {
    if (onRuleUpdate) {
      onRuleUpdate(ruleDetails);
    } else {
      if (validationMessage !== null) {
        return;
      }
      setRuleSaving(true);
      saveRule(ruleDetails)
        .then((ruleResponse) => {
          enqueueSnackbar("Rule successfully saved!", { variant: "success" });
          setRuleSaving(false);
          if (onRuleSaved) {
            onRuleSaved(ruleResponse);
          }
        })
        .catch((err) => {
          console.log(err);
          enqueueSnackbar("Rule not saved. Check the values and try again.", {
            variant: "error",
          });
          setRuleSaving(false);
        });
    }
  };
  return (
    <>
      <TextField
        label="Description"
        margin="normal"
        variant={"standard"}
        fullWidth
        InputLabelProps={{ shrink: true }}
        placeholder="Enter a rule description"
        value={ruleDetails?.description || ""}
        style={{ margin: "0px", paddingTop: "0px" }}
        disabled={disableRuleEdits}
        error={showDescriptionError}
        helperText={
          showDescriptionError ? "Add a description before saving." : undefined
        }
        onBlur={() => setDescriptionTouched(true)}
        onChange={(event) => {
          setDescriptionTouched(true);
          setRuleDetails({ ...ruleDetails, description: event.target.value });
        }}
      />
      <div className={classes.conditionsRow}>
        <Typography variant="h6" style={{ marginRight: "0.5em" }}>
          If
        </Typography>
        <Select
          disabled={disableRuleEdits}
          variant={"standard"}
          style={{ marginRight: "0.5em" }}
          value={ruleDetails ? ruleDetails.verb : ""}
          onChange={(event) =>
            setRuleDetails({ ...ruleDetails, verb: event.target.value as any })
          }
        >
          <MenuItem value={"ANY"}>ANY</MenuItem>
          <MenuItem value={"ALL"}>ALL</MenuItem>
        </Select>
        <Typography variant="h6">of the conditions are met</Typography>
        <Tooltip
          title={
            <Typography variant={"caption"}>
              ANY - at least one of the conditions specified below must be
              evaluated successfully for the actions to be applied.
              <br />
              <br />
              ALL - every single one of the conditions specified below must be
              evaluated successfully for the actions to be applied.
            </Typography>
          }
        >
          <InfoIcon style={{ width: "15px", marginLeft: "5px" }} />
        </Tooltip>
      </div>
      <div className={classes.ruleArea}>
        {ruleDetails?.conditions?.map(
          (condition: Condition, conditionIndex: number) => {
            return (
              <RuleCondition
                disableRuleEdits={disableRuleEdits}
                key={condition.id ?? `new-${conditionIndex}`}
                onAdd={onAddNewCondition(condition, conditionIndex)}
                onConditionChange={(changedCondition) => {
                  setRuleDetails((currentRuleDetails) => {
                    if (!currentRuleDetails) {
                      return currentRuleDetails;
                    }
                    const currentCondition =
                      currentRuleDetails.conditions?.[conditionIndex];
                    if (
                      JSON.stringify(currentCondition) ===
                      JSON.stringify(changedCondition)
                    ) {
                      return currentRuleDetails;
                    }
                    const newConditions = [...currentRuleDetails.conditions];
                    newConditions[conditionIndex] = changedCondition;
                    return {
                      ...currentRuleDetails,
                      conditions: newConditions,
                    };
                  });
                }}
                onRemove={onRemoveCondition(condition, conditionIndex)}
                conditionStatementsBySubject={conditionStatementsBySubject}
                selectedSubject={condition.subject}
                selectedStatement={condition.statement}
                userInput={condition.expectation}
                additionalAttributeId={condition.additional_attribute_id}
                ruleType={ruleType}
                conditionId={condition.id}
              />
            );
          }
        )}
      </div>
      <div className={classes.actionsRow}>
        <Typography variant="h6" style={{ marginRight: "0.5em" }}>
          Perform the following actions
        </Typography>
      </div>
      <div className={classes.ruleArea}>
        {ruleDetails?.actions?.map((action: Action, index: number) => {
          return (
            <RuleAction
              integratedSystemId={props.integratedSystemId}
              disableRuleEdits={disableRuleEdits}
              key={`${action.action_type}-${action.subjects}-${
                action.id || index
              }`}
              availableActions={availableActions}
              selectedAction={action.action_type}
              selectedActionValue={action.action_value}
              onAdd={onAddNewAction(action, index)}
              onRemove={onRemoveAction(action, index)}
              onActionChange={(changedAction) => {
                setRuleDetails((currentRuleDetails) => {
                  if (!currentRuleDetails) {
                    return currentRuleDetails;
                  }
                  const currentAction = currentRuleDetails.actions?.[index];
                  if (
                    JSON.stringify(currentAction) ===
                    JSON.stringify(changedAction)
                  ) {
                    return currentRuleDetails;
                  }
                  const newActions = [...currentRuleDetails.actions];
                  newActions[index] = changedAction;
                  return {
                    ...currentRuleDetails,
                    actions: newActions,
                  };
                });
              }}
            />
          );
        })}
      </div>
      {!disableSaveButton ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            mt: 2,
          }}
        >
          {validationMessage !== null &&
          !disableRuleEdits &&
          !showDescriptionError ? (
            <FormHelperText>{validationMessage}</FormHelperText>
          ) : null}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onRuleSave}
            disabled={ruleSaving === true || validationMessage !== null}
          >
            Save
          </Button>
        </Box>
      ) : null}
    </>
  );
}
